import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, RefreshCw, ShieldCheck } from 'lucide-react';
import { t } from '@/i18n';
import {
  useUsersApi,
  useCompaniesApi,
  type UserAdminListItem,
  DirectoryUnavailableError,
  DirectorySyncRunningError,
} from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { CompanyListItem } from '@ip/shared-types';
import { AddUserDialog } from './AddUserDialog';
import { UserPermissionsDrawer } from './UserPermissionsDrawer';

const COLUMN_COUNT = 6;

export function UserManagement() {
  const { listUsers, triggerDirectorySync } = useUsersApi();
  const { getCompanies } = useCompaniesApi();
  const { isSuperAdmin, permissions } = useAuth();

  const [users, setUsers] = useState<UserAdminListItem[]>([]);
  const [companies, setCompanies] = useState<CompanyListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [usersData, companiesData] = await Promise.all([listUsers(), getCompanies()]);
      setUsers(usersData);
      setCompanies(companiesData);
    } catch (error) {
      toast.error(t('errors.loadingFailed'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [listUsers, getCompanies]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleSyncDirectory = useCallback(async () => {
    try {
      setSyncing(true);
      const summary = await triggerDirectorySync();
      toast.success(
        t('users.syncDirectorySuccess', {
          updated: summary.updated,
          scanned: summary.scanned,
          members: summary.directory.directoryMembers,
          employees: summary.directory.employeesCreated,
        }),
      );
      if (summary.directory.stale > 0) {
        toast.warning(t('users.syncDirectoryStale', { stale: summary.directory.stale }));
      }
      await loadAll();
    } catch (error) {
      if (error instanceof DirectoryUnavailableError) {
        toast.error(t('users.syncDirectoryUnavailable'));
      } else if (error instanceof DirectorySyncRunningError) {
        toast.error(t('users.syncDirectoryRunning'));
      } else {
        toast.error(error instanceof Error ? error.message : t('users.syncDirectoryFailed'));
        console.error(error);
      }
    } finally {
      setSyncing(false);
    }
  }, [triggerDirectorySync, loadAll]);

  // Companies the acting admin may assign roles/grants in — superadmin: all
  // real companies; company admin: only those where they hold the 'admin'
  // role. GET /companies already returns only companies the caller can see
  // at all, so this is a further narrowing for the *management* actions.
  const manageableCompanies = useMemo(() => {
    const real = companies.filter((c) => c.nip !== 'other');
    if (isSuperAdmin) return real;
    return real.filter((c) => permissions?.companies[c.nip]?.roles.includes('admin'));
  }, [companies, isSuperAdmin, permissions]);

  const companyLabel = (nip: string) => companies.find((c) => c.nip === nip)?.name || nip;

  const selectedUser = users.find((u) => u.id === selectedUserId) ?? null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">{t('users.title')}</h1>
          <p className="text-muted-foreground text-sm">{t('users.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          {isSuperAdmin && (
            <Button variant="outline" onClick={handleSyncDirectory} disabled={syncing}>
              <RefreshCw className={`size-4 ${syncing ? 'animate-spin' : ''}`} />
              {t('users.syncDirectory')}
            </Button>
          )}
          <Button onClick={() => setAddOpen(true)} disabled={manageableCompanies.length === 0}>
            <Plus className="size-4" />
            {t('users.addUser')}
          </Button>
        </div>
      </div>

      <div className="bg-card rounded-xl border">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-muted-foreground whitespace-nowrap">
                  {t('users.columns.email')}
                </TableHead>
                <TableHead className="text-muted-foreground whitespace-nowrap">
                  {t('users.columns.name')}
                </TableHead>
                <TableHead className="text-muted-foreground whitespace-nowrap">
                  {t('users.columns.source')}
                </TableHead>
                <TableHead className="text-muted-foreground whitespace-nowrap">
                  {t('users.columns.roles')}
                </TableHead>
                <TableHead className="text-muted-foreground w-[110px] whitespace-nowrap">
                  {t('users.columns.status')}
                </TableHead>
                <TableHead className="text-muted-foreground w-[140px] whitespace-nowrap">
                  {t('common.actions')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }, (_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={COLUMN_COUNT}>
                      <Skeleton className="h-8 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="h-40 text-center">
                    <p className="font-medium">{t('users.noUsers')}</p>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="py-2 font-medium">{user.email}</TableCell>
                    <TableCell className="py-2">{user.displayName || '—'}</TableCell>
                    <TableCell className="py-2">
                      {user.authProvider === 'entra' ? t('users.source.entra') : t('users.source.local')}
                    </TableCell>
                    <TableCell className="py-2">
                      <div className="flex flex-wrap gap-1">
                        {user.roles.length === 0 ? (
                          <span className="text-muted-foreground text-xs">{t('users.noRoles')}</span>
                        ) : (
                          user.roles.map((role) => (
                            <Badge key={role.id} variant="outline" className="font-normal">
                              {t(`users.roleNames.${role.role}`)}
                              {role.companyNip && ` @ ${companyLabel(role.companyNip)}`}
                            </Badge>
                          ))
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-2">
                      <Badge variant={user.isActive ? 'secondary' : 'destructive'}>
                        {user.isActive ? t('users.status.active') : t('users.status.inactive')}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-2">
                      <Button variant="ghost" size="sm" onClick={() => setSelectedUserId(user.id)}>
                        <ShieldCheck className="size-4" />
                        {t('users.permissions')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <AddUserDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={loadAll}
        companies={manageableCompanies}
      />

      {selectedUser && (
        <UserPermissionsDrawer
          key={selectedUser.id}
          open
          user={selectedUser}
          companies={manageableCompanies}
          isSuperAdmin={isSuperAdmin}
          onClose={() => setSelectedUserId(null)}
          onChanged={loadAll}
        />
      )}
    </div>
  );
}
